import HomePageContainer from '@gameday/common/components/form/Common/HomePageContainer'
// keep this line for global css
import { InsuranceHead } from '@gameday/common/components/form/Common/Insurance/InsuranceHead'
import NotFoundPage from '@gameday/common/components/form/Common/NotFoundPage'
import { CornerHexagonBg } from '@gameday/react-components/components/Background/CornerHexagons'
import moment from 'moment-timezone'
import { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { Route } from 'react-router-dom'
import styled from 'styled-components'

import pjson from '../../../package.json'
import secondRoutes from '../../constants/routes/second'
import { getSubdomainInHash } from '../../utils/common'
import LoadingPage from '../common/LoadingPage'
import InitialForm from './pages/InitialForm'

const pageGrad = ''

const Gradient = styled.div`
  position: fixed;
  z-index: -1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-image: ${props =>
    (props.gradientOne || props.gradientTwo || props.formType !== 'membership') &&
    `linear-gradient(
      240deg,
      ${pageGrad.concat(props.gradientOne || props.gradientTwo || props.theme.colors.blue)},
      ${pageGrad.concat(props.gradientTwo || props.gradientOne || props.theme.colors.lightBlue)}
    )`};
  opacity: 0.9;
`

function RegoForm({
  getFormSettings,
  formSettings = {},
  classes,
  width,
  getPublicCert,
  showLoadingIndicator,
  isAuthenticated,
  userName,
  signout,
  setFormSettings,
  urlParams = {},
  formType = '',
  formError,
  loadingIndicatorContent,
  setFormSubdomain,
  publicCert,
  checkAuth,
  isCheckingAuth,
  teamsInEvent,
  teamsByUser,
}) {
  useEffect(() => {
    getPublicCert()
    const { orgId } = urlParams
    const { formId } = urlParams
    if (window.location.hash.includes('event/registration-form')) {
      setFormSubdomain({ subdomain: getSubdomainInHash() })
    }
    if (formId) {
      setFormSettings({
        formId,
        organisationId: orgId,
      })
    }
    getFormSettings()
  }, [])

  const { formId, expiryDate, timezone } = formSettings || {}

  useEffect(() => {
    if (publicCert && formId && expiryDate && timezone) {
      const end = moment.tz(expiryDate, timezone)
      const expired = formId && moment.tz(timezone).isAfter(end)

      checkAuth({ expired })
    }
  }, [publicCert, formId, expiryDate, timezone])

  const {
    formType: settingsFormType,
    landingPageImage,
    bgGradientPrimaryColor,
    bgGradientSecondaryColor,
    isInsuranceEnabled,
    insurance,
  } = formSettings || {}

  const appVersion = useMemo(() => pjson.version, [])

  let style = {}
  if (width !== 'xs') {
    let img = null
    // biome-ignore lint/suspicious/noAssignInExpressions: TODO
    landingPageImage && (img = landingPageImage)
    style = img
      ? {
          backgroundImage: `url(${img})`,
        }
      : {}
  }

  const routeItems = secondRoutes.map(route => (
    <Route key={route.id} exact={!route.noExact} path={route.path} component={route.component} />
  ))

  let pageTitle = ''
  switch ((formType || '').toLowerCase()) {
    case 'membership':
      pageTitle = 'Member Registration Form'
      break
    case 'shop':
      pageTitle = 'Shop Form'
      break
    default:
      pageTitle = 'Event Registration'
      break
  }

  const handleSignout = e => {
    if (window.confirm('Are you sure? You will lose all registration progress.')) {
      signout({ e })
    }
  }

  if (isCheckingAuth) {
    return <LoadingPage label="Loading..." />
  }

  // Archived forms are closed to members even mid-session or via deep links into the
  // wizard — render the same closed landing the entry route shows.
  if (formSettings.isArchived) {
    return <InitialForm />
  }

  // eslint-disable-next-line no-nested-ternary
  return formType ? (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`This is ${pageTitle}`} />
      </Helmet>
      {isInsuranceEnabled && <InsuranceHead insurance={insurance} />}
      {landingPageImage ? (
        <>
          <div alt="background" style={style} className={classes.bg} />
          <Gradient
            gradientOne={bgGradientPrimaryColor}
            gradientTwo={bgGradientSecondaryColor}
            formType={settingsFormType}
          />
        </>
      ) : (
        <CornerHexagonBg primaryColor={bgGradientPrimaryColor} secondaryColor={bgGradientSecondaryColor} useWindow />
      )}
      <HomePageContainer
        {...formSettings}
        showLoadingIndicator={showLoadingIndicator}
        loadingIndicatorContent={loadingIndicatorContent}
        isAuthenticated={isAuthenticated}
        userName={userName}
        logOutRequest={handleSignout}
        formType={formSettings.formType}
        teamsByUser={teamsByUser}
        teamsInEvent={teamsInEvent}
        appVersion={appVersion}
      >
        {routeItems}
      </HomePageContainer>
    </>
  ) : formError ? (
    <NotFoundPage />
  ) : (
    <LoadingPage label="Loading..." />
  )
}

export default RegoForm
